import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import { toast } from 'react-toastify';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function brandLabel(brand) {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function AddCardForm({ onSaved, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const setupRes = await api.post('payments/cards/setup-intent/');
      const clientSecret = setupRes.data.client_secret;
      const cardElement = elements.getElement(CardElement);

      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: cardholderName.trim() || undefined },
        },
      });

      if (setupError) {
        setError(setupError.message);
        setProcessing(false);
        return;
      }

      const pmId = setupIntent.payment_method;
      const confirmRes = await api.post('payments/cards/confirm-saved/', {
        payment_method_id: typeof pmId === 'string' ? pmId : pmId?.id,
        set_as_default: setAsDefault,
      });

      toast.success('Card saved successfully');
      onSaved(confirmRes.data.cards || []);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save card';
      setError(msg);
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border rounded-4 p-3" style={{ borderColor: '#E5E7EB', background: '#F8FAFC' }}>
      <h6 className="fw-bold mb-3">Add new card</h6>
      <div className="mb-3">
        <label className="form-label small fw-semibold">Cardholder name</label>
        <input
          type="text"
          className="form-control"
          placeholder="Name on card"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          disabled={processing}
        />
      </div>
      <div className="mb-3">
        <label className="form-label small fw-semibold">Card details</label>
        <div className="bg-white border rounded-3 p-3" style={{ borderColor: '#E5E7EB' }}>
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: { fontSize: '16px', color: '#1F2937', '::placeholder': { color: '#9CA3AF' } },
                invalid: { color: '#DC2626' },
              },
            }}
          />
        </div>
        <small className="text-muted d-block mt-2">
          Test: 4242 4242 4242 4242 · any future expiry · any CVC
        </small>
      </div>
      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="setDefaultNew"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
          disabled={processing}
        />
        <label className="form-check-label small" htmlFor="setDefaultNew">
          Set as default card
        </label>
      </div>
      {error && (
        <div className="alert alert-danger py-2 small" role="alert">{error}</div>
      )}
      <div className="d-flex gap-2">
        <button type="button" className="btn btn-outline-secondary rounded-3 flex-grow-1" onClick={onCancel} disabled={processing}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary rounded-3 flex-grow-1" disabled={!stripe || processing}>
          {processing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Saving...
            </>
          ) : (
            'Save card'
          )}
        </button>
      </div>
    </form>
  );
}

function UserSavedCards() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busyId, setBusyId] = useState(null);

  const loadCards = useCallback(async () => {
    try {
      const res = await api.get('payments/cards/');
      setCards(Array.isArray(res.data.cards) ? res.data.cards : []);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) navigate('/user/login');
      else toast.error(err.response?.data?.error || 'Failed to load cards');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const handleSetDefault = async (cardId) => {
    setBusyId(cardId);
    try {
      await api.post(`payments/cards/${cardId}/default/`);
      toast.success('Default card updated');
      await loadCards();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to set default');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm('Remove this card from your account?')) return;
    setBusyId(cardId);
    try {
      await api.delete(`payments/cards/${cardId}/delete/`);
      toast.success('Card removed');
      setCards((prev) => prev.filter((c) => c.id !== cardId));
      if (editingId === cardId) setEditingId(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete card');
    } finally {
      setBusyId(null);
    }
  };

  const handleUpdate = async (cardId) => {
    const name = editName.trim();
    if (!name) {
      toast.error('Enter cardholder name');
      return;
    }
    setBusyId(cardId);
    try {
      const res = await api.patch(`payments/cards/${cardId}/`, { cardholder_name: name });
      toast.success('Card updated');
      setCards((prev) =>
        prev.map((c) => (c.id === cardId ? { ...c, ...res.data.card } : c))
      );
      setEditingId(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update card');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F6FB' }}>
      <UserNavbar />
      <div className="container py-4 py-md-5" style={{ maxWidth: 640 }}>
        <div className="d-flex align-items-center mb-4">
          <button
            type="button"
            className="btn btn-link text-decoration-none text-muted p-0 me-3"
            onClick={() => navigate('/user/settings')}
          >
            <i className="bi bi-arrow-left fs-4" />
          </button>
          <div>
            <h2 className="fw-bold mb-0">Saved Cards</h2>
            <p className="text-muted small mb-0">Manage cards for faster checkout</p>
          </div>
        </div>

        <div className="bg-white rounded-4 border shadow-sm p-3 p-md-4" style={{ borderColor: '#E8EDF5' }}>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : (
            <>
              {cards.length === 0 && !showAdd && (
                <div className="text-center py-4">
                  <i className="bi bi-credit-card-2-front fs-1 text-muted d-block mb-2" />
                  <p className="text-muted mb-3">No saved cards yet</p>
                </div>
              )}

              <div className="d-flex flex-column gap-2 mb-3">
                {cards.map((card) => (
                  <div
                    key={card.id}
                    className="border rounded-3 p-3"
                    style={{ borderColor: card.is_default ? '#BFDBFE' : '#E5E7EB', background: card.is_default ? '#EFF6FF' : '#fff' }}
                  >
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="d-flex align-items-center gap-3 min-w-0">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                          style={{ width: 44, height: 44, background: '#DBEAFE', color: '#2563EB' }}
                        >
                          <i className="bi bi-credit-card-2-front fs-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="fw-bold mb-0 text-dark">
                            {brandLabel(card.brand)} •••• {card.last4}
                            {card.is_default && (
                              <span className="badge bg-primary ms-2" style={{ fontSize: 10 }}>Default</span>
                            )}
                          </p>
                          <small className="text-muted">
                            Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                            {card.cardholder_name ? ` · ${card.cardholder_name}` : ''}
                          </small>
                        </div>
                      </div>
                    </div>

                    {editingId === card.id ? (
                      <div className="mt-3">
                        <label className="form-label small fw-semibold">Cardholder name</label>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-2"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          disabled={busyId === card.id}
                        />
                        <p className="text-muted mb-2" style={{ fontSize: 11 }}>
                          Card number cannot be changed. To use a different card, add a new one and delete this.
                        </p>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-3"
                            onClick={() => setEditingId(null)}
                            disabled={busyId === card.id}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary rounded-3"
                            onClick={() => handleUpdate(card.id)}
                            disabled={busyId === card.id}
                          >
                            Save name
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {!card.is_default && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-3"
                            disabled={busyId === card.id}
                            onClick={() => handleSetDefault(card.id)}
                          >
                            Set default
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary rounded-3"
                          disabled={busyId === card.id}
                          onClick={() => {
                            setEditingId(card.id);
                            setEditName(card.cardholder_name || '');
                          }}
                        >
                          <i className="bi bi-pencil me-1" />
                          Update
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger rounded-3"
                          disabled={busyId === card.id}
                          onClick={() => handleDelete(card.id)}
                        >
                          <i className="bi bi-trash me-1" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {showAdd ? (
                <Elements stripe={stripePromise}>
                  <AddCardForm
                    onSaved={(nextCards) => {
                      setCards(nextCards);
                      setShowAdd(false);
                    }}
                    onCancel={() => setShowAdd(false)}
                  />
                </Elements>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary w-100 rounded-3 fw-semibold"
                  onClick={() => setShowAdd(true)}
                >
                  <i className="bi bi-plus-lg me-2" />
                  Add card
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserSavedCards;
